import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { APP_VERSION } from '../../config/env';
import { AppMode } from '../../enums/AppMode';
import { ErrorCodeList } from '../../enums/ErrorCodeList';
import { GlobalConfigurationContext } from '../../lib/context/GlobalConfigurationContext';
import { PERFORMANCE_MEASURE_NAMES } from '../../enums/DataDog/PerformanceMeasures';
import { QUERY_STRING } from '../../enums/QueryString';
import { SignOutMessage } from '../../enums/Auth';
import AppLoader from '../../components/AppLoader';
import AppRouter from '../../components/Router/AppRouter';
import AppRoutes from './AppRoutes';
import Datadog from '../../tools/Datadog';
import IntegrationService from '../../Services/IntegrationService';
import Layout from '../../components/Layout/Layout';
import SignInErrorDialog from '../../components/SignInErrorDialog';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import StorageProviderService from '../../Services/StorageProvider';
import useChromeExtension from '../../lib/hook/bootstrap/useChromeExtension';
import useHistory from '../../lib/hook/useBrowserHistory';
import useOrganizationObserver from '../../lib/hook/bootstrap/useOrganizationObserver';
import useOttExchange from '../../lib/hook/bootstrap/useOttExchange';
import useSignIn from '../../lib/hook/bootstrap/useSignIn';
import withBootstrap from '../../lib/hoc/withBootstrap';
import withProviders from '../../lib/hoc/withProviders';
import withSnackbar from '../../lib/hoc/withSnackbar';
import '../../style/root.css';
import '../../tools/Console';
import 'react-intl-tel-input/dist/main.css';

// Services configuration
IntegrationService.configure();
StorageProviderService.configure();

// 10s delay so we can avoid seeing the login page for vendors
const VENDOR_MODE_HIDE_LOGIN_TIMEOUT = 10 * 1000;
const PARAM_SIGN_OUT_MESSAGE = 'signOutMessage';
const PARAMS_OAUTH_ERROR = 'oauthError';

const LazyOauthErrorDialog = lazy(
  () => import('../../components/Auth/OAuth/OAuthErrorDialog')
);

const App: React.FC = (): JSX.Element => {
  const { appMode } = useContext(GlobalConfigurationContext);

  /**
   * AS a first step, if an OTT is given, it first waits for the session to be
   * exchanged before continuing with the app initialization. This makes the render
   * dependent on the OTT exchange to avoid any race conditions, as the app
   * depends on the sign in and the sign in depends on the OTT exchange.
   */
  const ottReady = useOttExchange();

  const history = useHistory();
  const isExtensionInstalled = useChromeExtension();
  const { isLoggedIn, isNewUser, isReady, signInError } = useSignIn(ottReady);

  const [hideLogin, setHideLogin] = useState(appMode === AppMode.Embedded);
  const [oauthError, setOauthError] = useState<ErrorCodeList>();

  const isAppReady = !!history && isReady;

  useOrganizationObserver({ isExtensionInstalled });

  useEffect(() => {
    if (APP_VERSION) {
      console.info(`Enable Us ${APP_VERSION}`);
    }

    const params = new URLSearchParams(window.location.search);

    handleSignOut(params);
    handleOAuthError(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hideLogin) {
      setTimeout(() => setHideLogin(false), VENDOR_MODE_HIDE_LOGIN_TIMEOUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideLogin]);

  useEffect(() => {
    if (isAppReady) {
      Datadog.sendCustomTiming(
        PERFORMANCE_MEASURE_NAMES.DSR_RENDERING_COMPLETED
      );
    }
  }, [isAppReady]);

  const handleSignOut = useCallback(
    (params: URLSearchParams) => {
      const signOut = !!params.get(QUERY_STRING.SIGN_OUT);
      const signOutMsg = params.get(PARAM_SIGN_OUT_MESSAGE);
      if (signOut && signOutMsg === SignOutMessage.SessionExpired) {
        setTimeout(() => {
          SnackbarStore.show('Your session has expired. Please sign in again.');
        }, 250);

        history.push({ search: '' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleOAuthError = useCallback(
    (params: URLSearchParams) => {
      const oauthErrorParam = params.get(PARAMS_OAUTH_ERROR) as ErrorCodeList;
      const handledErrors = [ErrorCodeList.OAuthUnauthorized];

      if (handledErrors.includes(oauthErrorParam)) {
        history.replace({ search: '' });
        setOauthError(oauthErrorParam);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onOauthErrorClose = () => {
    setOauthError(undefined);
  };

  return (
    <>
      {!isAppReady ? (
        <Layout main>
          <AppLoader />
        </Layout>
      ) : (
        <Suspense fallback={<AppLoader />}>
          <AppRouter>
            {(location) => (
              <AppRoutes
                hideLogin={hideLogin}
                isLoggedIn={isLoggedIn}
                isNewUser={isNewUser}
                location={location}
              />
            )}
          </AppRouter>
        </Suspense>
      )}

      <SignInErrorDialog errorMessage={signInError} />

      <Suspense fallback={<></>}>
        {oauthError && (
          <LazyOauthErrorDialog
            error={oauthError}
            onClose={onOauthErrorClose}
          />
        )}
      </Suspense>
    </>
  );
};

/**
 * Composing the App with Higher Order Components in this order:
 *  - withBootstrap: provides the app with the bootstrap logic (specifically the region setup)
 *  - withProviders: wraps the app with several providers (Error Boundaries, I18n, etc.)
 *  - withSnackbar: sets the snackbar as a global component (so we can show messages from anywhere)
 *                  using SnackbarStore to manage when and what to show
 */
export default withBootstrap(withProviders(withSnackbar(App)));
